import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
 HiOutlineViewGrid,
 HiOutlineDatabase,
 HiOutlineMap,
 HiOutlineOfficeBuilding,
 HiOutlineLocationMarker,
 HiOutlineAcademicCap,
 HiOutlineChevronDown,
 HiOutlineChevronDoubleLeft,
 HiOutlineChevronDoubleRight,
 HiOutlineCalendar,
 HiOutlineClipboardList,
 HiOutlineUserAdd,
 HiOutlineRefresh,
 HiOutlinePuzzle,
 // HiOutlinePlay, // Commented out - used in Simulator link
 HiOutlineDocumentReport,
 HiOutlineTrash,
} from "react-icons/hi";
import { useUserRole } from "../hooks/useUserRole";

function Sidebar({ collapsed, onToggle }) {
 const { canSeeUsers, canViewCycles, isAdmin, canManageCompetencies } = useUserRole();
 const location = useLocation();
 const [masterDataOpen, setMasterDataOpen] = useState(false);
 const [cyclesOpen, setCyclesOpen] = useState(false);
 const [questionBankOpen, setQuestionBankOpen] = useState(false);
 const [competencyOpen, setCompetencyOpen] = useState(false);
 const [reportingOpen, setReportingOpen] = useState(false);
 const [quarterlyOpen, setQuarterlyOpen] = useState(false);
 const [syncLogsOpen, setSyncLogsOpen] = useState(false);

 useEffect(() => {
 if (location.pathname.startsWith("/master-data")) {
 setMasterDataOpen(true);
 }
 if (location.pathname.startsWith("/cycles")) {
 setCyclesOpen(true);
 }
 if (location.pathname.startsWith("/question-bank")) {
 setQuestionBankOpen(true);
 }
 if (location.pathname.startsWith("/competency")) {
 setCompetencyOpen(true);
 }
 if (location.pathname.startsWith("/reporting")) {
 setReportingOpen(true);
 }
 if (location.pathname.startsWith("/quarterly")) {
 setQuarterlyOpen(true);
 }
 if (location.pathname.startsWith("/sync-logs")) {
 setSyncLogsOpen(true);
 }
 }, [location.pathname]);

 const linkBase =
 "group flex items-center gap-2.5 py-[6px] px-3 mx-2 text-[#57606a] no-underline text-[13px] font-medium border-none bg-transparent w-[calc(100%-16px)] text-left cursor-pointer rounded-lg transition-all duration-150 relative";
 const linkCollapsed = "!mx-auto !w-10 !px-0 justify-center";
 const linkHover = "hover:text-[#24292f] hover:bg-[#f3f4f6]";
 const linkActive =
 "!text-[#0078d4] !bg-[#eff6ff] !font-semibold before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-4 before:bg-[#0078d4] before:rounded-r-full";
 const linkActiveCollapsed = "!text-[#0078d4] !bg-[#eff6ff] !font-semibold before:hidden";

 const sublinkBase =
 "flex items-center gap-2 py-[5px] px-2.5 my-[1px] text-[#57606a] no-underline text-[12px] font-normal rounded-md transition-all duration-150";
 const sublinkHover = "hover:text-[#24292f] hover:bg-[#f3f4f6]";
 const sublinkActive =
 "!text-[#0078d4] !bg-[#eff6ff] !font-semibold";

 return (
 <aside
 className={`fixed top-0 left-0 bottom-0 ${
 collapsed ? "w-16" : "w-60"
 } bg-[#f3f2f1] border-r border-[#e5e7eb] text-[#57606a] flex flex-col z-[100] overflow-y-auto overflow-x-hidden transition-[width] duration-200 ease-in-out`}
 >
 {/* Brand + collapse toggle — same row when expanded; when collapsed there's
     no room for both side by side, so the toggle stays on its own row below. */}
 <div
 className={`flex items-center py-3 ${
 collapsed ? "px-3 justify-center" : "px-4 justify-between gap-3"
 }`}
 >
 <div className="flex items-center gap-3 min-w-0">
 <div className="w-8 h-8 bg-[#0078d4] rounded-lg flex items-center justify-center font-bold text-sm text-white tracking-tight flex-shrink-0">
 S
 </div>
 {!collapsed && (
 <div className="flex flex-col min-w-0">
 <span className="text-[13px] font-bold text-[#24292f] tracking-tight leading-tight truncate">
 SLO Admin
 </span>
 <span className="text-[9px] text-[#8b949e] font-medium uppercase tracking-[0.1em] mt-0.5 truncate">
 Accelerator
 </span>
 </div>
 )}
 </div>
 {!collapsed && (
 <button
 type="button"
 onClick={onToggle}
 title="Collapse sidebar"
 aria-label="Collapse sidebar"
 className="flex items-center justify-center w-7 h-7 rounded-md text-[#8b949e] bg-transparent border-none cursor-pointer hover:text-[#24292f] hover:bg-[#f3f4f6] transition-colors duration-150 flex-shrink-0"
 >
 <HiOutlineChevronDoubleLeft className="text-[13px]" />
 </button>
 )}
 </div>

 {/* Expand toggle — collapsed state only, own row (no space beside the logo) */}
 {collapsed && (
 <div className="flex justify-center px-2 pb-2">
 <button
 type="button"
 onClick={onToggle}
 title="Expand sidebar"
 aria-label="Expand sidebar"
 className="flex items-center justify-center w-7 h-7 rounded-md text-[#8b949e] bg-transparent border-none cursor-pointer hover:text-[#24292f] hover:bg-[#f3f4f6] transition-colors duration-150"
 >
 <HiOutlineChevronDoubleRight className="text-[13px]" />
 </button>
 </div>
 )}

 {/* Divider */}
 <div className="mx-4 h-px bg-[#e5e7eb]" />

 {/* Main Navigation */}
 <nav className="flex-1 py-2">
 {/* Overview */}
 <div className="mb-1">
 {!collapsed && (
 <span className="block px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b949e]">
 Overview
 </span>
 )}
 <NavLink
 to="/dashboard"
 title="Dashboard"
 className={({ isActive }) =>
 `${linkBase} ${linkHover} ${isActive ? (collapsed ? linkActiveCollapsed : linkActive) : ""} ${
 collapsed ? linkCollapsed : ""
 }`
 }
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlineViewGrid />
 </span>
 {!collapsed && <span>Dashboard</span>}
 </NavLink>
 </div>

 {/* Assessment */}
 {canViewCycles && (
 <div className="mt-2">
 {!collapsed && (
 <span className="block px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b949e]">
 Assessment
 </span>
 )}
 <div>
  <button
   title="Cycles"
   className={`${linkBase} ${linkHover} ${
    location.pathname.startsWith("/cycles") && !cyclesOpen
     ? (collapsed ? linkActiveCollapsed : linkActive)
     : ""
   } ${collapsed ? linkCollapsed : ""}`}
   onClick={() => setCyclesOpen(!cyclesOpen)}
  >
   <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
    <HiOutlineRefresh />
   </span>
   {!collapsed && (
    <>
     <span>Cycles</span>
     <span
      className={`ml-auto text-[10px] flex items-center text-[#8b949e] transition-transform duration-200 ${
       cyclesOpen ? "rotate-180" : ""
      }`}
     >
      <HiOutlineChevronDown />
     </span>
    </>
   )}
  </button>

  {!collapsed && (
  <div
   className={`overflow-hidden transition-all duration-300 ease-in-out ${
    cyclesOpen ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
   }`}
  >
   <div className="flex flex-col mt-0.5 ml-[28px] mr-2 pl-2.5 border-l border-[#e5e7eb]">
    <NavLink
     to="/cycles/continuous"
     className={({ isActive }) =>
      `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
     }
    >
     <span className="text-xs w-4 flex items-center justify-center">
      <HiOutlineRefresh />
     </span>
     Continuous Cycle
    </NavLink>
    <NavLink
     to="/cycles/quarterly"
     className={({ isActive }) =>
      `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
     }
    >
     <span className="text-xs w-4 flex items-center justify-center">
      <HiOutlineClipboardList />
     </span>
     Quarterly Cycle
    </NavLink>
   </div>
  </div>
  )}
 </div>
 </div>
 )}

 {/* User Management */}
 <div className="mt-2">
 {!collapsed && (
 <span className="block px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b949e]">
 User Management
 </span>
 )}

 {/* Users */}
 {canSeeUsers && (
 <NavLink
 to="/user-management/users"
 title="Users"
 className={({ isActive }) =>
 `${linkBase} ${linkHover} ${isActive ? (collapsed ? linkActiveCollapsed : linkActive) : ""} ${
 collapsed ? linkCollapsed : ""
 }`
 }
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlineUserAdd />
 </span>
 {!collapsed && <span>Users</span>}
 </NavLink>
 )}
 </div>

 {/* Data Management */}
 <div className="mt-2">
 {!collapsed && (
 <span className="block px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b949e]">
 Data Management
 </span>
 )}
 <div>
 <button
 title="Master Data"
 className={`${linkBase} ${linkHover} relative ${
 location.pathname.startsWith("/master-data") && !masterDataOpen
 ? (collapsed ? linkActiveCollapsed : linkActive)
 : ""
 } ${collapsed ? linkCollapsed : ""}`}
 onClick={() => setMasterDataOpen(!masterDataOpen)}
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlineDatabase />
 </span>
 {!collapsed && (
 <>
 <span>Master Data</span>
 <span
 className={`ml-auto text-[10px] flex items-center text-[#8b949e] transition-transform duration-200 ${
 masterDataOpen ? "rotate-180" : ""
 }`}
 >
 <HiOutlineChevronDown />
 </span>
 </>
 )}
 </button>

 {!collapsed && (
 <div
 className={`overflow-hidden transition-all duration-300 ease-in-out ${
 masterDataOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
 }`}
 >
 <div className="flex flex-col mt-0.5 ml-[28px] mr-2 pl-2.5 border-l border-[#e5e7eb]">
 <NavLink
 to="/master-data/states"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineMap />
 </span>
 States
 </NavLink>
 <NavLink
 to="/master-data/districts"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineOfficeBuilding />
 </span>
 Districts
 </NavLink>
 <NavLink
 to="/master-data/blocks"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineLocationMarker />
 </span>
 Blocks
 </NavLink>
 <NavLink
 to="/master-data/schools"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineAcademicCap />
 </span>
 Schools
 </NavLink>
 <NavLink
 to="/master-data/assessment-years"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineClipboardList />
 </span>
 Assessment Years
 </NavLink>
 <NavLink
 to="/master-data/holidays"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineCalendar />
 </span>
 Holidays
 </NavLink>

 </div>
 </div>
 )}
 </div>
 </div>

 {/* Competency */}
 <div className="mt-2">
 {!collapsed && (
 <span className="block px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b949e]">
 Competency
 </span>
 )}
 <div>
 <button
 title="Competency"
 className={`${linkBase} ${linkHover} relative ${
 location.pathname.startsWith("/competency") && !competencyOpen
 ? (collapsed ? linkActiveCollapsed : linkActive)
 : ""
 } ${collapsed ? linkCollapsed : ""}`}
 onClick={() => setCompetencyOpen(!competencyOpen)}
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlinePuzzle />
 </span>
 {!collapsed && (
 <>
 <span>Competency</span>
 <span
 className={`ml-auto text-[10px] flex items-center text-[#8b949e] transition-transform duration-200 ${
 competencyOpen ? "rotate-180" : ""
 }`}
 >
 <HiOutlineChevronDown />
 </span>
 </>
 )}
 </button>

 {!collapsed && (
 <div
 className={`overflow-hidden transition-all duration-300 ease-in-out ${
 competencyOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
 }`}
 >
 <div className="flex flex-col mt-0.5 ml-[28px] mr-2 pl-2.5 border-l border-[#e5e7eb]">
 <NavLink
 to="/competency/list"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlinePuzzle />
 </span>
 Competency
 </NavLink>
 <NavLink
 to="/competency/category"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlinePuzzle />
 </span>
 Category
 </NavLink>
 <NavLink
 to="/competency/header"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlinePuzzle />
 </span>
 Header
 </NavLink>
 </div>
 </div>
 )}
 </div>
 </div>

 {/* Question Bank */}
 <div className="mt-2">
 {!collapsed && (
 <span className="block px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b949e]">
 Question Bank
 </span>
 )}
 <div>
 <button
 title="Question Bank"
 className={`${linkBase} ${linkHover} relative ${
 location.pathname.startsWith("/question-bank") && !questionBankOpen
 ? (collapsed ? linkActiveCollapsed : linkActive)
 : ""
 } ${collapsed ? linkCollapsed : ""}`}
 onClick={() => setQuestionBankOpen(!questionBankOpen)}
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlineClipboardList />
 </span>
 {!collapsed && (
 <>
 <span>Question Bank</span>
 <span
 className={`ml-auto text-[10px] flex items-center text-[#8b949e] transition-transform duration-200 ${
 questionBankOpen ? "rotate-180" : ""
 }`}
 >
 <HiOutlineChevronDown />
 </span>
 </>
 )}
 </button>

 {!collapsed && (
 <div
 className={`overflow-hidden transition-all duration-300 ease-in-out ${
 questionBankOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
 }`}
 >
 <div className="flex flex-col mt-0.5 ml-[28px] mr-2 pl-2.5 border-l border-[#e5e7eb]">
 <NavLink
 to="/question-bank/sets"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineClipboardList />
 </span>
 Sets & Questions
 </NavLink>
 {/* Simulator - HIDDEN FOR NOW */}
 {/* <NavLink
 to="/question-bank/simulator"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlinePlay />
 </span>
 Simulator
 </NavLink> */}
 </div>
 </div>
 )}
 </div>
 </div>

 {/* Reporting & Analytics */}
 {canViewCycles && (
 <div className="mt-2">
 {!collapsed && (
 <span className="block px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b949e]">
 Reporting & Analytics
 </span>
 )}
 <div>
 <button
 title="Continuous Cycle Reports"
 className={`${linkBase} ${linkHover} relative ${
 location.pathname.startsWith("/reporting") && !reportingOpen
 ? (collapsed ? linkActiveCollapsed : linkActive)
 : ""
 } ${collapsed ? linkCollapsed : ""}`}
 onClick={() => setReportingOpen(!reportingOpen)}
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlineDocumentReport />
 </span>
 {!collapsed && (
 <>
 <span>Continuous Cycle Reports</span>
 <span
 className={`ml-auto text-[10px] flex items-center text-[#8b949e] transition-transform duration-200 ${
 reportingOpen ? "rotate-180" : ""
 }`}
 >
 <HiOutlineChevronDown />
 </span>
 </>
 )}
 </button>

 {!collapsed && (
 <div
 className={`overflow-hidden transition-all duration-300 ease-in-out ${
 reportingOpen ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
 }`}
 >
 <div className="flex flex-col mt-0.5 ml-[28px] mr-2 pl-2.5 border-l border-[#e5e7eb]">
 <NavLink
 to="/reporting/continuous-acceleration"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineDocumentReport />
 </span>
 School Wise Report
 </NavLink>
 <NavLink
 to="/reporting/student-level"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineDocumentReport />
 </span>
 Student Level Report
 </NavLink>
 </div>
 </div>
 )}
 </div>

 <div>
 <button
 title="Quarterly Reports"
 className={`${linkBase} ${linkHover} relative ${
 location.pathname.startsWith("/quarterly") && !quarterlyOpen
 ? (collapsed ? linkActiveCollapsed : linkActive)
 : ""
 } ${collapsed ? linkCollapsed : ""}`}
 onClick={() => setQuarterlyOpen(!quarterlyOpen)}
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlineDocumentReport />
 </span>
 {!collapsed && (
 <>
 <span>Quarterly Reports</span>
 <span
 className={`ml-auto text-[10px] flex items-center text-[#8b949e] transition-transform duration-200 ${
 quarterlyOpen ? "rotate-180" : ""
 }`}
 >
 <HiOutlineChevronDown />
 </span>
 </>
 )}
 </button>

 {!collapsed && (
 <div
 className={`overflow-hidden transition-all duration-300 ease-in-out ${
 quarterlyOpen ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
 }`}
 >
 <div className="flex flex-col mt-0.5 ml-[28px] mr-2 pl-2.5 border-l border-[#e5e7eb]">
 <NavLink
 to="/quarterly/qlr"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineDocumentReport />
 </span>
 QLR Report
 </NavLink>
 </div>
 </div>
 )}
 </div>
 </div>
 )}

 </nav>

 {/* Bottom Section */}
 <div className="mx-4 h-px bg-[#e5e7eb]" />
 <div className="p-2 pb-4">
 {isAdmin && (
 <div>
 <button
 title="Logs"
 className={`${linkBase} ${linkHover} relative ${
 location.pathname.startsWith("/sync-logs") && !syncLogsOpen
 ? (collapsed ? linkActiveCollapsed : linkActive)
 : ""
 } ${collapsed ? linkCollapsed : ""}`}
 onClick={() => setSyncLogsOpen(!syncLogsOpen)}
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlineRefresh />
 </span>
 {!collapsed && (
 <>
 <span>Logs</span>
 <span
 className={`ml-auto text-[10px] flex items-center text-[#8b949e] transition-transform duration-200 ${
 syncLogsOpen ? "rotate-180" : ""
 }`}
 >
 <HiOutlineChevronDown />
 </span>
 </>
 )}
 </button>
 {!collapsed && (
 <div
 className={`overflow-hidden transition-all duration-300 ease-in-out ${
 syncLogsOpen ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
 }`}
 >
 <div className="flex flex-col mt-0.5 ml-[28px] mr-2 pl-2.5 border-l border-[#e5e7eb]">
 <NavLink
 to="/sync-logs"
 className={({ isActive }) =>
 `${sublinkBase} ${sublinkHover} ${isActive ? sublinkActive : ""}`
 }
 >
 <span className="text-xs w-4 flex items-center justify-center">
 <HiOutlineRefresh />
 </span>
 Sync Logs
 </NavLink>
 </div>
 </div>
 )}
 </div>
 )}

 {canManageCompetencies && (
 <NavLink
 to="/restore-center"
 title="Restore Center"
 className={({ isActive }) =>
 `${linkBase} ${linkHover} ${isActive ? (collapsed ? linkActiveCollapsed : linkActive) : ""} ${
 collapsed ? linkCollapsed : ""
 }`
 }
 >
 <span className="text-base w-5 flex items-center justify-center flex-shrink-0">
 <HiOutlineTrash />
 </span>
 {!collapsed && <span>Restore Center</span>}
 </NavLink>
 )}
 </div>
 </aside>
 );
}

export default Sidebar;
